import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories, mainSiteUrl } from "@/mocks/categories";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  const navBg = isScrolled
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
    : "bg-black/30 backdrop-blur-[6px]";

  const brandColor = isScrolled ? "text-foreground-950" : "text-background-50";
  const outlineBtnClasses = isScrolled
    ? "border border-background-200/60 hover:bg-background-100/60 text-foreground-800"
    : "border border-background-50/50 hover:bg-background-50/15 text-background-50";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 h-16 transition-all duration-300 ${navBg}`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="w-full h-full px-4 md:px-8 flex items-center justify-between">
          {/* Left: Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-background-50/80 hover:text-background-50"
              }`}
            >
              Home
            </Link>
            <Link
              to="/article/kakobuy-buying-guide"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                isScrolled ? "text-foreground-700 hover:text-foreground-950" : "text-background-50/80 hover:text-background-50"
              }`}
            >
              Shopping Guide
            </Link>

            {/* Category Dropdown */}
            <div className="relative flex items-center" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                  isScrolled
                    ? "bg-background-100/80 hover:bg-background-200/80 text-foreground-800 border-background-200/50"
                    : "bg-background-50/15 hover:bg-background-50/25 text-background-50 border-background-50/30"
                }`}
                aria-expanded={isDropdownOpen}
                aria-haspopup="true"
              >
                <span>Select Category</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/60 shadow-lg py-1 max-h-80 overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.seq}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 whitespace-nowrap"
                      onClick={() => setIsDropdownOpen(false)}
                    >
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Center: Brand */}
          <div className="md:absolute md:left-1/2 md:-translate-x-1/2 flex items-center gap-2">
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center rounded-full bg-primary-500">
              <i className="ri-store-2-line text-background-50 text-sm md:text-base"></i>
            </div>
            <span className={`font-heading font-semibold text-base md:text-lg whitespace-nowrap ${brandColor}`}>
              KakobuySpreadsheet
            </span>
          </div>

          {/* Right: Desktop CTA Buttons */}
          <div className="hidden md:flex items-center gap-2 md:gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${outlineBtnClasses}`}
            >
              kakobuy spreadsheet
              <i className="ri-external-link-line text-xs"></i>
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-xs"></i>
            </a>
          </div>

          {/* Mobile: Hamburger + Buy It Now */}
          <div className="flex md:hidden items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                isScrolled ? "text-foreground-800 hover:bg-background-200/60" : "text-background-50 hover:bg-background-50/15"
              }`}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Fullscreen Menu — rendered outside nav to avoid stacking context clipping */}
      <div
        className={`fixed inset-0 top-16 bg-background-50 z-[60] transition-transform duration-300 md:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="h-full overflow-y-auto px-4 py-6 flex flex-col gap-4">
          {/* Home */}
          <Link
            to="/"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 font-medium text-sm cursor-pointer"
          >
            <i className="ri-home-4-line text-lg"></i>
            Home
          </Link>

          {/* Shopping Guide */}
          <Link
            to="/article/kakobuy-buying-guide"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 text-foreground-900 font-medium text-sm cursor-pointer"
          >
            <i className="ri-book-open-line text-lg"></i>
            Shopping Guide
          </Link>

          {/* Categories Accordion */}
          <div>
            <button
              onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
              className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-background-100 text-foreground-900 font-medium text-sm cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line text-lg"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${isMobileCatOpen ? "rotate-180" : ""}`}></i>
            </button>
            <div
              className={`overflow-hidden transition-all duration-300 ${
                isMobileCatOpen ? "max-h-[600px] mt-2" : "max-h-0"
              }`}
            >
              <div className="grid grid-cols-2 gap-2 px-2">
                {categories.map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2.5 rounded-lg text-foreground-600 hover:text-foreground-900 hover:bg-background-200/60 text-xs font-medium transition-colors text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* kakobuy spreadsheet CTA */}
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setIsMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-foreground-300 text-foreground-700 font-medium text-sm cursor-pointer"
          >
            kakobuy spreadsheet
            <i className="ri-external-link-line text-xs"></i>
          </a>

          {/* SEO Article Links */}
          <div className="border-t border-background-200/60 pt-4">
            <p className="text-xs text-foreground-400 font-medium mb-3 px-1">Guides & Resources</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Beginner Guide", slug: "kakobuy-spreadsheet-guide" },
                { label: "Trusted Sellers", slug: "trusted-kakobuy-sellers" },
                { label: "Safety Guide", slug: "is-kakobuy-safe" },
                { label: "Buying Guide", slug: "kakobuy-buying-guide" },
                { label: "Shipping Guide", slug: "kakobuy-shipping-guide" },
                { label: "Quality Check", slug: "kakobuy-quality-check" },
                { label: "Size Guide", slug: "kakobuy-size-guide" },
                { label: "Budget Finds", slug: "kakobuy-budget-finds" },
              ].map((item) => (
                <Link
                  key={item.slug}
                  to={`/article/${item.slug}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-3 py-2 rounded-lg text-foreground-500 hover:text-foreground-900 hover:bg-background-200/60 text-xs transition-colors text-center cursor-pointer"
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}